import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, CircleCheck, CreditCard, RotateCw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { getApiErrorMessage } from "@/services/apiClient";
import paymentIntegrationService from "@/services/paymentIntegrationService";
import { showError, showInfo, showWarning } from "@/store/errorStore";
import InfinitePayFormModal from "./InfinitePayFormModal";
import PaymentIntegrationCard from "./PaymentIntegrationCard";

const TITLE = "Integrações de Pagamento";

// Formulário de configuração por provedor (slug da rota). Provedor novo na
// API = novo modal aqui; os demais aparecem como "Em breve".
const CONFIG_MODALS = {
  infinitepay: InfinitePayFormModal,
};

// 400 (dados inválidos / não configurada) e 409 (version desatualizada) são tratados na tela
const SILENT = { silentStatuses: [400, 409] };

/** Erros HTTP já abrem o modal global; aqui só falhas locais (sem resposta da API) */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, TITLE);
  }
};

/**
 * Integrações de pagamento online da empresa via /payment-integrations.
 * Cada empresa configura os próprios provedores e mantém só um ativo: ativar
 * um desativa o outro (regra do backend). O cardápio usa a integração ativa
 * pela rota unificada /online-payments.
 */
function PaymentIntegrationsManagement() {
  const { companyCode, isGlobalOwner } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [configTarget, setConfigTarget] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyProvider, setBusyProvider] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // Owner global em "todas as empresas": a API exige uma empresa no contexto (TENANT001)
  const needsCompany = isGlobalOwner && !companyCode;

  /** `background`: recarrega sem esqueleto; se falhar, o modal global avisa */
  const loadData = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const data = await paymentIntegrationService.getAll({ silent: !background });
      const list = Array.isArray(data) ? data : [];
      setIntegrations(list);
      setLoadError(false);
      return list;
    } catch (error) {
      if (!background) setLoadError(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (needsCompany) {
      setLoading(false);
      return;
    }
    loadData();
  }, [loadData, needsCompany]);

  // Implementados primeiro (InfinitePay no topo), mantendo a ordem da API
  const sorted = useMemo(
    () =>
      integrations
        .map((integration, index) => ({ integration, index }))
        .sort((a, b) => Number(b.integration.implemented) - Number(a.integration.implemented) || a.index - b.index)
        .map((e) => e.integration),
    [integrations]
  );

  const active = useMemo(() => integrations.find((i) => i.enabled) || null, [integrations]);

  /* ---------- Ações ---------- */

  const openConfigure = useCallback((integration) => {
    setConfigTarget(integration);
    setShowModal(true);
  }, []);

  /** 409: alguém alterou antes; recarrega e reabre com a versão nova */
  const handleConflict = async (provider) => {
    showWarning(
      "Esta integração foi alterada por outra pessoa. Os dados foram recarregados; confira e salve de novo.",
      TITLE
    );
    const list = await loadData({ background: true });
    const fresh = list?.find((i) => i.provider === provider);
    if (fresh) setConfigTarget(fresh);
  };

  const handleSave = async (payload) => {
    const target = configTarget;
    if (!target) return;
    setSaving(true);
    try {
      const saved = await paymentIntegrationService.save(target.provider, payload, SILENT);
      const name = target.providerName || target.provider;
      showInfo(
        saved?.enabled ?? payload.enabled
          ? `${name} configurada e ativa nos pagamentos online`
          : `${name} configurada (inativa)`,
        TITLE
      );
      setShowModal(false);
      await loadData({ background: true });
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        await handleConflict(target.provider);
      } else if (status === 400) {
        showError(getApiErrorMessage(error) || "Dados inválidos", TITLE);
      } else {
        showLocalError(error, "Erro ao salvar a integração");
      }
    } finally {
      setSaving(false);
    }
  };

  const setEnabled = async (integration, enabled) => {
    const name = integration.providerName || integration.provider;
    setBusyProvider(integration.provider);
    try {
      await paymentIntegrationService.setEnabled(integration.provider, enabled, SILENT);
      showInfo(enabled ? `${name} ativada nos pagamentos online` : `${name} desativada`, TITLE);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 400 || status === 409) {
        showError(getApiErrorMessage(error) || "Não foi possível alterar a integração", TITLE);
      } else {
        showLocalError(error, "Erro ao alterar a integração");
      }
    } finally {
      setBusyProvider(null);
      await loadData({ background: true });
    }
  };

  // Ativar pede confirmação só quando troca a integração ativa
  const handleActivate = (integration) => {
    if (active && active.provider !== integration.provider) setActivateTarget(integration);
    else setEnabled(integration, true);
  };

  const handleDeactivate = useCallback((integration) => setDeactivateTarget(integration), []);

  const confirmActivate = () => {
    const target = activateTarget;
    setActivateTarget(null);
    if (target) setEnabled(target, true);
  };

  const confirmDeactivate = () => {
    const target = deactivateTarget;
    setDeactivateTarget(null);
    if (target) setEnabled(target, false);
  };

  const ConfigModal = CONFIG_MODALS[configTarget?.provider] || null;
  const initialLoading = loading && integrations.length === 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <div className="min-w-0">
        <h1 className="truncate font-display text-xl font-bold tracking-tight text-primary md:text-2xl">{TITLE}</h1>
        <p className="text-xs text-muted-foreground md:text-sm">
          Provedores para o cliente pagar online pelo cardápio. Só um pode ficar ativo por vez.
        </p>
      </div>

      {needsCompany ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Building2 className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">Escolha uma empresa</p>
          <p className="mt-1 text-sm text-muted-foreground">
            As integrações de pagamento são configuradas por empresa. Selecione a empresa no topo da tela.
          </p>
        </div>
      ) : (
        <>
          {/* Integração em uso */}
          {initialLoading ? (
            <Skeleton className="h-[72px] rounded-xl" aria-hidden="true" />
          ) : (
            !loadError && (
              <div className="flex items-center gap-3 rounded-xl border bg-card p-3.5 shadow-soft">
                <span
                  className={
                    active
                      ? "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-primary"
                      : "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-terra-soft text-terra"
                  }
                >
                  {active ? <CircleCheck className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />}
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Pagamento online
                  </p>
                  <p className="truncate text-sm font-semibold text-foreground">
                    {active
                      ? `Ativo com ${active.providerName || active.provider}`
                      : "Desligado: o cardápio não oferece \"Pagar online\""}
                  </p>
                </div>
              </div>
            )
          )}

          {initialLoading ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-[170px] rounded-2xl" />
              ))}
            </div>
          ) : loadError && integrations.length === 0 ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
              <CreditCard className="mb-3 h-12 w-12 text-muted-foreground/40" />
              <p className="font-display text-base font-semibold text-foreground">
                Não foi possível carregar as integrações
              </p>
              <p className="mt-1 text-sm text-muted-foreground">Verifique a conexão e tente de novo.</p>
              <button
                type="button"
                onClick={() => loadData()}
                className="mt-4 flex h-9 items-center gap-1 rounded-full border border-border bg-surface px-3.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
              >
                <RotateCw className="h-4 w-4" />
                Tentar novamente
              </button>
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
              <CreditCard className="mb-3 h-12 w-12 text-muted-foreground/40" />
              <p className="font-display text-base font-semibold text-foreground">Nenhum provedor disponível</p>
              <p className="mt-1 text-sm text-muted-foreground">A API não retornou provedores de pagamento.</p>
            </div>
          ) : (
            <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label="Provedores de pagamento">
              {sorted.map((integration) => (
                <PaymentIntegrationCard
                  key={integration.provider}
                  integration={{
                    ...integration,
                    // Sem formulário no painel ainda: trata como "Em breve"
                    implemented: integration.implemented && Boolean(CONFIG_MODALS[integration.provider]),
                  }}
                  busy={busyProvider === integration.provider}
                  onConfigure={openConfigure}
                  onActivate={handleActivate}
                  onDeactivate={handleDeactivate}
                />
              ))}
            </section>
          )}
        </>
      )}

      {ConfigModal && (
        <ConfigModal
          open={showModal}
          onOpenChange={setShowModal}
          integration={configTarget}
          activeOther={active && active.provider !== configTarget?.provider ? active : null}
          onSave={handleSave}
          loading={saving}
        />
      )}

      {/* Trocar a integração ativa */}
      <AlertDialog open={Boolean(activateTarget)} onOpenChange={(open) => !open && setActivateTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Trocar a integração ativa?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">{active?.providerName}</span> será desativada e os
              novos pagamentos online passarão a usar{" "}
              <span className="font-semibold text-foreground">{activateTarget?.providerName}</span>. Pagamentos já
              iniciados continuam sendo verificados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmActivate}
              className="rounded-full bg-primary text-primary-foreground hover:bg-brand-dark"
            >
              Ativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Desativar */}
      <AlertDialog open={Boolean(deactivateTarget)} onOpenChange={(open) => !open && setDeactivateTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar pagamento online?</AlertDialogTitle>
            <AlertDialogDescription>
              Com <span className="font-semibold text-foreground">{deactivateTarget?.providerName}</span>{" "}
              desativada, o cardápio deixa de oferecer "Pagar online". Pagamentos já iniciados continuam sendo
              verificados e a configuração é mantida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeactivate}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PaymentIntegrationsManagement;
